/**
 * The two Stage 1 checks an intake must answer before a file is opened, and the
 * confirmations that answer them.
 *
 * Both compare the SNB records on the intake with what the file claims, and
 * both are allowed to disagree — a PAN can list a lot that is not part of the
 * complaint, and a parcel can genuinely sit across a boundary — but neither may
 * disagree *silently*. The person opening the file either fixes the intake or
 * says why it is right, and that reason is kept on the file and in its audit
 * trail. Shared by the intake form, which shows the warnings as they arise, and
 * by the API, which refuses a file that leaves one unanswered.
 */

export const INTAKE_CONFIRMATION_KINDS = [
  /** A PID the PAN lists, with no Parcel record on the file, is not part of this matter. */
  'pid_not_in_matter',
  /** A parcel whose record places it outside the file's local government belongs on this file. */
  'parcel_outside_local_government',
] as const;
export type IntakeConfirmationKind = (typeof INTAKE_CONFIRMATION_KINDS)[number];

/**
 * What the intake form sends: which check, which PID, and why. Validated
 * structurally in the service, like `parcels` — no nested-type decorators.
 */
export interface IntakeConfirmationInput {
  kind: IntakeConfirmationKind;
  pid: string;
  reason: string;
}

/** Shortest reason accepted, after trimming. "n/a" is not a reason. */
export const INTAKE_CONFIRMATION_REASON_MIN = 3;
export const INTAKE_CONFIRMATION_REASON_MAX = 1000;

/** A confirmation as recorded on the file. */
export interface IntakeConfirmation {
  kind: IntakeConfirmationKind;
  pid: string;
  reason: string;
  /**
   * What the check saw when it was answered, so the record still reads
   * correctly after the file's anchor or records change: the parcel's place
   * and the file's local government for a location check, the PAN for a
   * missing-parcel check.
   */
  parcelPlace?: string | null;
  localGovernmentName?: string | null;
  pan?: string | null;
  confirmedByUserId: string | null;
  confirmedByName: string | null;
  confirmedAt: string;
}

/** An answer the intake still owes. */
export interface IntakeCheckFinding {
  kind: IntakeConfirmationKind;
  pid: string;
  /** Location check only: where the parcel's record places it. */
  parcelPlace?: string;
  /** Location check only: the local government the parcel was checked against. */
  localGovernmentName?: string;
}

/**
 * Identifies one finding *as it was shown*. A location answer is bound to the
 * place the record gave and the local government it was checked against, so
 * changing either asks again rather than carrying a confirmation over to a
 * question nobody answered.
 */
export function intakeCheckKey(
  f: Pick<IntakeCheckFinding, 'kind' | 'pid' | 'parcelPlace' | 'localGovernmentName'>,
): string {
  return f.kind === 'parcel_outside_local_government'
    ? [f.kind, f.pid, f.parcelPlace ?? '', f.localGovernmentName ?? ''].join('|')
    : [f.kind, f.pid].join('|');
}

// ── Missing Parcel records ─────────────────────────────────────────────────

/**
 * PIDs the PAN enumerates that no Parcel record on the intake covers.
 *
 * Only a PAN listing more than one PID raises this: with one, the PAN and the
 * file's parcel are the same lot, and the form's softer reminder to upload its
 * Parcel record is enough. With several, a missing record is a lot the file
 * either covers without its land detail or should not name at all — the case
 * the spec says must be decided rather than left.
 */
export function missingPanParcelPids(panPids: readonly string[], parcelPids: readonly string[]) {
  const listed = [...new Set(panPids.map((p) => p.trim()).filter(Boolean))];
  if (listed.length < 2) return [];
  const have = new Set(parcelPids.map((p) => p.trim()).filter(Boolean));
  return listed.filter((pid) => !have.has(pid));
}

// ── Parcel location vs. local government ───────────────────────────────────

/** Where a Parcel record places the land. The PAN is never asked: see below. */
export interface ParcelLocation {
  placeName?: string | null;
  taxingAuthority?: string | null;
  parish?: string | null;
}

/** The names a local government answers to, today and before amalgamation. */
export interface LocalGovernmentNames {
  name: string;
  officialNameEn?: string | null;
  officialNameFr?: string | null;
  formerNames?: readonly string[] | null;
}

// The statutory class, in either language, leading or trailing a name. SNB
// prints the same municipality as "SAINT-LEONARD", "Town of Saint-Léonard" and
// "Saint-Léonard Town"; all three are one place.
const CLASS_WORDS = [
  'regional municipality of',
  'rural community of',
  'rural district of',
  'municipality of',
  'local service district of',
  'lsd of',
  'city of',
  'town of',
  'village of',
  'parish of',
  'municipalite regionale de',
  'communaute rurale de',
  'district rural de',
  'district de services locaux de',
  'municipalite de',
  'paroisse de',
  'cite de',
  'ville de',
  'village de',
  'ville d',
];
// Statutory qualifiers SNB may print after a name ("Grand Falls Parish",
// "Miramichi City LSD"). Only these are dropped: any other word makes it a
// different place — "Upper Miramichi" is not Miramichi.
const CLASS_SUFFIXES = [
  'local service district',
  'rural community',
  'regional municipality',
  'municipality',
  'city',
  'town',
  'village',
  'parish',
  'lsd',
  'ville',
  'cite',
  'paroisse',
  'district de services locaux',
  'dsl',
];

/** One spelling for a place name: no accents, case, punctuation or class words. */
export function normalizePlaceName(raw: string | null | undefined): string {
  let s = (raw ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[’'`]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  // "St." and "Ste" are how SNB abbreviates the saints the registry spells out.
  s = s.replace(/\bste\b/g, 'sainte').replace(/\bst\b/g, 'saint');
  for (const prefix of CLASS_WORDS) {
    if (s.startsWith(`${prefix} `)) {
      s = s.slice(prefix.length + 1);
      break;
    }
  }
  // Repeatedly, so a stacked qualifier ("city lsd") comes off too.
  for (let stripped = true; stripped;) {
    stripped = false;
    for (const suffix of CLASS_SUFFIXES) {
      if (s.endsWith(` ${suffix}`)) {
        s = s.slice(0, -(suffix.length + 1)).trim();
        stripped = true;
        break;
      }
    }
  }
  return s.trim();
}

/** The parcel's place as a person would name it, for the warning. */
export function parcelPlaceLabel(location: ParcelLocation): string | null {
  return (
    [location.placeName, location.taxingAuthority, location.parish]
      .map((v) => v?.trim())
      .find((v): v is string => !!v) ?? null
  );
}

/**
 * Whether a Parcel record places the land in this local government.
 *
 * Read from the Parcel (PID) record only — its place name, taxing authority and
 * parish. Never the PAN: the PAN is the assessment account and shows where the
 * tax bill goes, and an owner living in another municipality is normal, not a
 * sign the file is in the wrong place.
 *
 * Any of the local government's names counts, including the communities it
 * absorbed: parcels are still recorded under pre-amalgamation names years
 * after the fact. `null` means the record names no place at all, which is not a
 * mismatch — there is nothing to warn about, only nothing to confirm with.
 */
export function parcelInLocalGovernment(
  location: ParcelLocation,
  lg: LocalGovernmentNames,
): boolean | null {
  const places = [location.placeName, location.taxingAuthority, location.parish]
    .map(normalizePlaceName)
    .filter(Boolean);
  if (places.length === 0) return null;
  const names = [lg.name, lg.officialNameEn, lg.officialNameFr, ...(lg.formerNames ?? [])]
    .map(normalizePlaceName)
    .filter(Boolean);
  // Exact after normalising: qualifiers are already stripped, and a name that
  // merely contains the municipality's ("Miramichi River Valley") is another
  // registry entry.
  return places.some((place) => names.includes(place));
}

/**
 * The Parcel records that place their land outside the file's local government,
 * one finding per PID. Records that name no place, and files with no anchor,
 * raise nothing. Pass records oldest first: for a PID uploaded twice the last
 * record is the one checked, as the API does — a re-upload is a correction.
 */
export function parcelsOutsideLocalGovernment(
  parcels: readonly (ParcelLocation & { pid?: string | null })[],
  lg: LocalGovernmentNames | null,
): IntakeCheckFinding[] {
  if (!lg) return [];
  // A Map keeps each PID where it first appeared but the record it last saw.
  const latest = new Map<string, ParcelLocation>();
  for (const parcel of parcels) {
    const pid = parcel.pid?.trim();
    if (pid) latest.set(pid, parcel);
  }
  const out: IntakeCheckFinding[] = [];
  for (const [pid, parcel] of latest) {
    if (parcelInLocalGovernment(parcel, lg) === false) {
      out.push({
        kind: 'parcel_outside_local_government',
        pid,
        parcelPlace: parcelPlaceLabel(parcel) ?? undefined,
        localGovernmentName: lg.name,
      });
    }
  }
  return out;
}

/** Findings the given confirmations leave unanswered. */
export function unansweredFindings(
  findings: readonly IntakeCheckFinding[],
  confirmations: readonly Pick<IntakeConfirmationInput, 'kind' | 'pid' | 'reason'>[] | undefined,
): IntakeCheckFinding[] {
  const answered = new Set(
    (confirmations ?? [])
      .filter((c) => (c.reason?.trim().length ?? 0) >= INTAKE_CONFIRMATION_REASON_MIN)
      .map((c) => `${c.kind}:${c.pid.trim()}`),
  );
  return findings.filter((f) => !answered.has(`${f.kind}:${f.pid}`));
}
